import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { TarjetaAyuda } from '../marco/cabecera';
import { Ayuda } from './Ayuda';
import { Chip, type EstadoChip } from './Chip';
import { Icono } from './Icono';
import type { NombreIcono } from './iconos';

/**
 * RV Panel (componentes.md §10): el único panel lateral para crear, editar y ver una ficha. Ordenador: fijo a la derecha
 * (12 px de los bordes), radio 24, entra deslizándose (440 ms); `formulario` mide min(560, 100vw − 24) y `ficha`
 * min(72vw, 980). Con `ayuda`, la «?» en la cabecera, antes de cerrar y de la acción (sin tarjetas, no hay «?»). Móvil (< 1024): hoja desde abajo con un ligero rebote, asa arriba, «×» de 44 y el botón principal en el
 * pie, a mano del pulgar. Fondo `--rv-scrim` (tocarlo cierra), Esc cierra, `role="dialog"` y `aria-modal`. Cabecera fija
 * que gana una línea al bajar; contenido con scroll propio.
 *
 * Lo que aún no hace (piezas del módulo 1, que lo necesitarán con sus pantallas): RV Botonera de las fichas con apartados,
 * arrastrar la hoja para cerrarla, la media hoja y el retroceso del fondo en móvil.
 */
interface Props {
    abierto: boolean;
    onCerrar: () => void;
    movil?: boolean;
    tamano?: 'formulario' | 'ficha';
    titulo: string;
    eyebrow?: string;
    subtitulo?: string;
    chip?: string;
    chipEstado?: EstadoChip;
    icono?: NombreIcono;
    /** Tarjetas de la «?» del panel (`[[icono, título, texto]]` del diseño); sin tarjetas, no hay «?». */
    ayuda?: readonly TarjetaAyuda[];
    /** Texto del botón principal ('' = sin botón). */
    accion?: string;
    accionIcono?: NombreIcono;
    ocupado?: boolean;
    ocupadoTexto?: string;
    inactiva?: boolean;
    onAccion?: () => void;
    cerrar?: string;
    children: ReactNode;
}

export function PanelLateral({ abierto, onCerrar, movil = false, tamano = 'formulario', titulo, eyebrow, subtitulo, chip, chipEstado = 'neutral', icono, ayuda = [], accion = '', accionIcono = 'check', ocupado = false, ocupadoTexto = 'Guardando…', inactiva = false, onAccion, cerrar = 'Cerrar', children }: Props) {
    const idTitulo = useId();
    const cuerpo = useRef<HTMLDivElement>(null);
    const [linea, setLinea] = useState(false);

    useEffect(() => {
        if (!abierto) {
            return;
        }
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onCerrar();
            }
        };
        window.addEventListener('keydown', esc);
        return () => {
            window.removeEventListener('keydown', esc);
        };
    }, [abierto, onCerrar]);

    const ancho = tamano === 'ficha' ? 'min(72vw, 980px)' : 'min(560px, calc(100vw - 24px))';
    const hayAccion = accion !== '';
    const botonAccion = (
        <button
            type="button"
            className={`rv-b rv-b-pri${movil ? ' rv-b--movil' : ''}`}
            disabled={inactiva || ocupado}
            aria-busy={ocupado}
            style={{ opacity: inactiva ? 0.4 : 1 }}
            onClick={onAccion}
        >
            {ocupado ? <Icono nombre="loader-circle" tamano={18} className="rv-gira" /> : <Icono nombre={accionIcono} />}
            <span>{ocupado ? ocupadoTexto : accion}</span>
        </button>
    );

    return (
        <>
            <div aria-hidden="true" className="rv-panel__velo" onClick={onCerrar} style={{ opacity: abierto ? 1 : 0, pointerEvents: abierto ? 'auto' : 'none' }} />
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby={idTitulo}
                aria-hidden={!abierto}
                className={`rv-panel ${movil ? 'rv-panel--movil' : 'rv-panel--ordenador'}`}
                style={{
                    width: movil ? undefined : ancho,
                    visibility: abierto ? 'visible' : 'hidden',
                    transform: abierto ? 'none' : movil ? 'translateY(calc(100% + 64px))' : 'translateX(calc(100% + 40px))',
                    ['--rv-p-retardo' as string]: abierto ? '0s' : '440ms',
                }}
            >
                {movil && <span className="rv-panel__asa" aria-hidden="true" />}
                <div className={`rv-panel__cabecera${linea ? ' rv-panel__cabecera--linea' : ''}`}>
                    <div className="rv-panel__fila">
                        {icono !== undefined && (
                            <span className="rv-panel__baldosa">
                                <Icono nombre={icono} />
                            </span>
                        )}
                        <span style={{ flex: 1 }} />
                        <Ayuda tarjetas={ayuda} />
                        {movil ? (
                            <button type="button" className="rv-bi" aria-label={cerrar} style={{ border: 'none', background: 'var(--rv-paper)' }} onClick={onCerrar}>
                                <Icono nombre="x" tamano={20} />
                            </button>
                        ) : (
                            <>
                                <button type="button" className="rv-b rv-b-sec" onClick={onCerrar}>
                                    <Icono nombre="x" />
                                    <span>{cerrar}</span>
                                </button>
                                {hayAccion && botonAccion}
                            </>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {eyebrow !== undefined && <span className="rv-panel__ceja">{eyebrow}</span>}
                        <h2 id={idTitulo} className="rv-panel__titulo">
                            {titulo}
                        </h2>
                        {subtitulo !== undefined && <span className="rv-panel__sub">{subtitulo}</span>}
                        {chip !== undefined && (
                            <span style={{ display: 'flex', marginTop: 4 }}>
                                <Chip estado={chipEstado}>{chip}</Chip>
                            </span>
                        )}
                    </div>
                </div>
                <div
                    ref={cuerpo}
                    className="rv-panel__cuerpo"
                    onScroll={() => {
                        setLinea((cuerpo.current?.scrollTop ?? 0) > 0);
                    }}
                >
                    {children}
                </div>
                {movil && hayAccion && <div className="rv-panel__pie">{botonAccion}</div>}
            </section>
        </>
    );
}
